import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import { 
  notificationApi, 
  notificationApiHelpers, 
  StoredNotification 
} from '../services/notificationApi';
import { notificationHiddenState } from '../services/notificationHiddenState';

// 🔒 SECURITY: User-aware storage to prevent cross-user data leakage
let currentNotificationUserId: string | null = null;

const getNotificationStoreKey = (userId: string): string => {
  return `notification-storage-${userId}`;
};

/**
 * Set the current user ID for notification storage.
 * This must be called before the store rehydrates from localStorage.
 */
export const setNotificationStoreUserId = (userId: string | null): void => {
  const userLabel = userId ? `${userId.substring(0, 20)}...` : 'anonymous';
  console.log(`🔐 [Notification Store Storage] User context: ${userLabel}`);
  currentNotificationUserId = userId;
};

// 🔥 OPTIMIZED: Smart storage with immediate writes for critical operations
let pendingWrite: NodeJS.Timeout | null = null;
let currentStoredValue: string | null = null;
let previousNotificationCount = 0;
const WRITE_DEBOUNCE_MS = 500; // Wait 500ms before writing to localStorage (non-critical updates)

// Flush any pending localStorage writes before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (pendingWrite && currentStoredValue) {
      clearTimeout(pendingWrite);
      const key = currentNotificationUserId 
        ? getNotificationStoreKey(currentNotificationUserId) 
        : 'notification-storage-anonymous';
      console.log(`🚨 [Notification Store Storage] Flushing pending write before unload`);
      localStorage.setItem(key, currentStoredValue);
      pendingWrite = null;
    }
  });
}

const debouncedUserAwareNotificationStorage: StateStorage = {
  getItem: (_name: string) => {
    const key = currentNotificationUserId 
      ? getNotificationStoreKey(currentNotificationUserId) 
      : 'notification-storage-anonymous';
    const value = localStorage.getItem(key);
    
    if (value) {
      console.log(`📖 [Notification Store Storage] Reading from key: ${key.substring(0, 50)}...`);
      // Track initial notification count
      try {
        const parsed = JSON.parse(value);
        if (parsed?.state?.notifications) {
          previousNotificationCount = parsed.state.notifications.length;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return value;
  },
  
  setItem: (_name: string, value: string) => {
    const key = currentNotificationUserId 
      ? getNotificationStoreKey(currentNotificationUserId) 
      : 'notification-storage-anonymous';
    
    currentStoredValue = value;
    
    // 🔥 CRITICAL FIX: Detect if this is a new notification being added
    let isCriticalUpdate = false;
    try {
      const parsed = JSON.parse(value);
      const currentNotificationCount = parsed?.state?.notifications?.length || 0;
      
      // Critical update: new notification added or panel state changed
      isCriticalUpdate = (
        currentNotificationCount > previousNotificationCount || // New notification
        parsed?.state?.isVisible !== undefined // Panel visibility changed
      );
      
      previousNotificationCount = currentNotificationCount;
    } catch (e) {
      // If parsing fails, treat as critical to be safe
      isCriticalUpdate = true;
    }
    
    // Cancel any pending write
    if (pendingWrite) {
      clearTimeout(pendingWrite);
      pendingWrite = null;
    }
    
    if (isCriticalUpdate) {
      // 🔥 IMMEDIATE WRITE for critical updates (new notifications, panel state)
      console.log(`⚡ [Notification Store Storage] IMMEDIATE write (critical): ${key.substring(0, 50)}...`);
      localStorage.setItem(key, value);
    } else {
      // Debounce non-critical updates (read status changes, etc.)
      pendingWrite = setTimeout(() => {
        console.log(`💾 [Notification Store Storage] Debounced write: ${key.substring(0, 50)}...`);
        localStorage.setItem(key, value);
        pendingWrite = null;
      }, WRITE_DEBOUNCE_MS);
    }
  },
  
  removeItem: (_name: string) => {
    const key = currentNotificationUserId 
      ? getNotificationStoreKey(currentNotificationUserId) 
      : 'notification-storage-anonymous';
    
    // Cancel any pending write
    if (pendingWrite) {
      clearTimeout(pendingWrite);
      pendingWrite = null;
    }
    
    currentStoredValue = null;
    previousNotificationCount = 0;
    
    console.log(`🗑️ [Notification Store Storage] Removing key: ${key.substring(0, 50)}...`);
    localStorage.removeItem(key);
  }
};

export interface Notification {
  id: string;
  type: 'position_opened' | 'position_modified' | 'position_closed' | 'trade_closed' | 'milestone' | 'system';
  title: string;
  message: string;
  timestamp: number;
  isRead: boolean;
  isHidden?: boolean; // Local hide state (not persisted)
  isDismissed?: boolean; // Local dismiss state (not persisted)
  source: 'sse' | 'stored' | 'manual'; // Track notification source for deduplication
  originalEventId?: string; // Link to database event ID for stored notifications
  clickAction?: {
    type: 'navigate' | 'external' | 'modal';
    payload: any;
  };
  metadata?: {
    symbol?: string;
    account?: string;
    pnl?: number;
    tradeId?: string | number; // Updated to support both string and number for closed position navigation
    side?: string;
    strategy?: string;
    exectype?: string;
    // 🔥 NEW: Event timestamp fields
    eventTimestamp?: number; // When the actual event occurred
    isRecoveryEvent?: boolean; // Whether this is from session gap recovery
    sessionGapDuration?: number; // How long the gap was (in ms)
    // 🔥 NEW: Unified notifications support
    roi?: number; // ROI percentage for trades
    milestoneType?: string; // Type of milestone reached
    changes?: any; // Position changes for modifications
    
    // 🔥 NEW: Position state tracking for navigation
    positionStatus?: 'open' | 'closed';  // Explicit position state
    isLivePosition?: boolean;            // Quick boolean check
    
    // Enhanced routing context
    navigationType?: 'live-chart' | 'closed-chart' | 'filtered-list';
    targetPath?: string;                 // Pre-computed navigation path
    
    // Allow any additional properties for flexibility
    [key: string]: any;
  };
}

export interface NotificationSettings {
  enabledTypes: {
    position_opened: boolean;
    position_modified: boolean;
    position_closed: boolean;
    trade_closed: boolean;
    milestone: boolean;
    system: boolean;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isVisible: boolean;
  notificationSettings: NotificationSettings;
  panelMode: 'notifications' | 'settings';
  sessionStartTime: number; // Track session start for recovery detection
  
  // 🔥 NEW: Real-time event tracking
  hasRealtimeUpdates: boolean; // Track if there are pending real-time updates
  lastRealtimeUpdate: number; // Timestamp of last SSE notification
  
  // 🔥 NEW: Stored notifications support
  storedNotifications: StoredNotification[];
  isLoadingStored: boolean;
  lastSyncTimestamp: number | null;
  storedNotificationsError: string | null;
  
  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'isRead'>) => void;
  integrateStoredNotification: (notification: Notification) => void;
  integrateStoredNotificationsBatch: (notifications: Notification[]) => { added: number; skipped: number }; // NEW: For stored notifications with preserved read status
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  markAllAsReadOnOpen: () => Promise<void>;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
  hideNotification: (id: string) => void; // Hide locally (not persistent)
  dismissNotification: (id: string) => void; // Dismiss locally (not persistent)
  hideAllNotifications: () => void; // Hide all locally
  togglePanel: () => void;
  setVisible: (visible: boolean) => void;
  
  // 🔥 NEW: Real-time tracking actions
  clearRealtimeUpdates: () => void;
  
  // New notification settings actions
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => void;
  setPanelMode: (mode: 'notifications' | 'settings') => void;
  toggleNotificationType: (type: keyof NotificationSettings['enabledTypes']) => void;
  resetNotificationSettings: () => void;
  getSortedNotifications: () => Notification[];
  getFilteredUnreadCount: () => number;
  
  // 🔥 NEW: Stored notifications actions
  fetchStoredNotifications: (options?: {
    limit?: number;
    offset?: number;
    eventTypes?: string[];
    since?: Date;
  }) => Promise<void>;
  syncWithStoredNotifications: () => Promise<void>;
  markStoredAsRead: (id: string) => Promise<void>;
  clearStoredNotificationsError: () => void;
  
  // 🔥 NEW: Deduplication helper methods
  isDuplicateOfSSEEvent: (stored: StoredNotification) => boolean;
  isRecoveryNotification: (stored: StoredNotification) => boolean;
  
  // Internal methods
  _generateId: () => string;
  _updateUnreadCount: () => void;
  _cleanupOldNotifications: () => void;
}

// Constants for notification management
const MAX_NOTIFICATIONS = 50;
const CLEANUP_DAYS = 7;
const CLEANUP_MS = CLEANUP_DAYS * 24 * 60 * 60 * 1000;
const THROTTLE_WINDOW = 5000; // 5 seconds throttle for same type/symbol combinations

// Default settings (all enabled for existing users)
const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  enabledTypes: {
    position_opened: true,
    position_modified: true,
    position_closed: true,
    trade_closed: true,
    milestone: true,
    system: true,
  },
};

// Throttling map for high-frequency events
const throttleMap = new Map<string, number>();

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isVisible: false,
      notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
      panelMode: 'notifications' as const,
      sessionStartTime: Date.now(), // Initialize session start time
      
      // 🔥 NEW: Real-time event tracking
      hasRealtimeUpdates: false,
      lastRealtimeUpdate: 0,
      
      // 🔥 NEW: Stored notifications state
      storedNotifications: [],
      isLoadingStored: false,
      lastSyncTimestamp: null,
      storedNotificationsError: null,

      addNotification: (notificationData: Omit<Notification, 'id' | 'timestamp' | 'isRead'>) => {
        const state = get();
        const id = state._generateId();
        // 🔥 FIX: Use actual event timestamp for proper "time ago" display
        // Fall back to current time only if no event timestamp is available
        const timestamp = notificationData.metadata?.eventTimestamp || Date.now();
        
        const newNotification: Notification = {
          ...notificationData,
          id,
          timestamp,
          isRead: false,
          source: notificationData.source || 'manual', // Default to manual if not specified
        };

        // Generate throttle key for high-frequency event throttling
        const throttleKey = `${newNotification.type}:${newNotification.metadata?.symbol || ''}:${newNotification.metadata?.account || ''}`;
        const lastThrottleTime = throttleMap.get(throttleKey) || 0;
        
        // 🔥 CRITICAL FIX: Only throttle truly high-frequency events, not important user notifications
        const shouldThrottle = (
          // Only throttle non-critical position_modified events
          newNotification.type === 'position_modified' && 
          timestamp - lastThrottleTime < THROTTLE_WINDOW
        );
        
        if (shouldThrottle) {
          console.log(`🔄 Throttling high-frequency notification: ${newNotification.type} for ${newNotification.metadata?.symbol}`);
          return;
        }
        
        // Update throttle map
        throttleMap.set(throttleKey, timestamp);

        // Enhanced duplicate detection with cross-source deduplication for position events
        const isDuplicate = state.notifications.some((notification: Notification) => {
          const isBasicDuplicate = notification.type === newNotification.type &&
            notification.metadata?.symbol === newNotification.metadata?.symbol &&
            notification.metadata?.account === newNotification.metadata?.account;
          
          // For position_opened events, check across sources (SSE vs unified) within a longer window
          if (newNotification.type === 'position_opened' && isBasicDuplicate) {
            const timeDiff = Math.abs(notification.timestamp - timestamp);
            
            // Allow longer window for cross-source deduplication (5 minutes)
            // This handles cases where unified events come after SSE events
            const CROSS_SOURCE_WINDOW = 5 * 60 * 1000; // 5 minutes
            
            if (timeDiff < CROSS_SOURCE_WINDOW) {
              // Check if this is a cross-source duplicate
              const isDifferentSource = notification.source !== newNotification.source;
              
              if (isDifferentSource) {
                console.log(`🔄 Cross-source duplicate detected: ${newNotification.metadata?.symbol} (${notification.source} vs ${newNotification.source})`);
                
                // Priority logic: prefer stored/unified over SSE when both exist for same event
                // This is because stored events are more reliable and have proper "while away" context
                const existingIsStored = notification.source === 'stored';
                const newIsStored = newNotification.source === 'stored';
                
                if (!existingIsStored && newIsStored) {
                  // Replace SSE notification with stored notification
                  console.log(`🔄 Replacing SSE notification with stored version: ${newNotification.metadata?.symbol}`);
                  const existingIndex = state.notifications.findIndex((n: Notification) => n.id === notification.id);
                  if (existingIndex >= 0) {
                    state.notifications.splice(existingIndex, 1);
                  }
                  return false; // Allow the new stored notification
                } else if (existingIsStored && !newIsStored) {
                  // Keep existing stored notification, skip new SSE notification
                  console.log(`🔄 Keeping stored notification, skipping SSE duplicate: ${newNotification.metadata?.symbol}`);
                  return true; // Skip the new SSE notification
                }
              }
              
              // Same source or timing-based duplicate within shorter window
              return timeDiff < 30000; // 30 seconds for same source
            }
          }
          
          // Special handling for milestone notifications to prevent stored vs recovery duplicates
          if (newNotification.type === 'milestone' && isBasicDuplicate) {
            const existingPercentage = notification.metadata?.milestone_percentage;
            const newPercentage = newNotification.metadata?.milestone_percentage;
            const existingIsStored = notification.source === 'stored';
            const newIsStored = newNotification.source === 'stored';
            
            // If both have the same milestone percentage, they're duplicates
            if (existingPercentage !== undefined && newPercentage !== undefined && 
                Math.abs(parseFloat(String(existingPercentage)) - parseFloat(String(newPercentage))) < 0.1) {
              // Prioritize stored notifications over recovery/manual ones
              if (existingIsStored && !newIsStored) {
                console.log(`🔄 Skipping duplicate milestone (keeping stored): ${newNotification.metadata?.symbol} ${newPercentage}%`);
                return true; // Skip the new one, keep existing stored notification
              } else if (!existingIsStored && newIsStored) {
                console.log(`🔄 Replacing milestone with stored version: ${newNotification.metadata?.symbol} ${newPercentage}%`);
                // Remove the existing non-stored notification to replace it with stored one
                const existingIndex = state.notifications.findIndex((n: Notification) => n.id === notification.id);
                if (existingIndex >= 0) {
                  state.notifications.splice(existingIndex, 1);
                }
                return false; // Allow the new stored notification
              }
              return true; // Both same source, it's a duplicate
            }
          }
          
          // Default timing-based duplicate detection for other types
          return isBasicDuplicate && Math.abs(notification.timestamp - timestamp) < 30000; // 30 seconds
        });

        if (isDuplicate) {
          console.log(`🔄 Skipping duplicate notification: ${newNotification.type} for ${newNotification.metadata?.symbol}`);
          return;
        }

        set((state: NotificationState) => {
          const updatedNotifications = [newNotification, ...state.notifications];
          
          // Trim to max notifications and cleanup old ones
          const cleanedNotifications = updatedNotifications
            .filter((n: Notification) => Date.now() - n.timestamp < CLEANUP_MS)
            .slice(0, MAX_NOTIFICATIONS);
          
          return {
            ...state,
            notifications: cleanedNotifications,
          };
        });
        
        // Update unread count using consistent filtering
        get()._updateUnreadCount();
        
        // 🔥 EVENT-DRIVEN: Track real-time updates for SSE notifications
        if (newNotification.source === 'sse') {
          set((state: NotificationState) => ({
            ...state,
            hasRealtimeUpdates: true,
            lastRealtimeUpdate: Date.now()
          }));
          console.log(`🔔 [NotificationStore] Real-time SSE notification tracked: ${newNotification.type}`);
        }

        // Log notification creation
        console.log(`🔔 New notification added: ${newNotification.type} - ${newNotification.title}`);
      },

      integrateStoredNotification: (storedNotification: Notification) => {
        const state = get();
        
        // Check for duplicates
        const exists = state.notifications.some((n: Notification) => n.id === storedNotification.id);
        if (exists) {
          console.log(`🔄 Skipping duplicate stored notification: ${storedNotification.id}`);
          return;
        }

        // Add the stored notification with its preserved read status
        set((state: NotificationState) => {
          const updatedNotifications = [storedNotification, ...state.notifications];
          
          // Trim to max notifications and cleanup old ones
          const cleanedNotifications = updatedNotifications
            .filter((n: Notification) => Date.now() - n.timestamp < CLEANUP_MS)
            .slice(0, MAX_NOTIFICATIONS);
          
          return {
            ...state,
            notifications: cleanedNotifications,
          };
        });
        
        // Update unread count using consistent filtering
        get()._updateUnreadCount();

        // Log stored notification integration
        console.log(`📥 Stored notification integrated: ${storedNotification.type} - ${storedNotification.title} (Read: ${storedNotification.isRead})`);
      },

      // 🔥 NEW: Batch integration method to reduce localStorage writes
      integrateStoredNotificationsBatch: (storedNotifications: Notification[]) => {
        const state = get();
        
        if (storedNotifications.length === 0) {
          console.log('📥 [NotificationStore] No stored notifications to integrate');
          return { added: 0, skipped: 0 };
        }
        
        let addedCount = 0;
        let skippedCount = 0;
        
        // Filter out duplicates BEFORE state update
        const newNotifications = storedNotifications.filter((stored: Notification) => {
          const exists = state.notifications.some((n: Notification) => n.id === stored.id);
          if (exists) {
            skippedCount++;
            return false;
          }
          addedCount++;
          return true;
        });
        
        if (newNotifications.length === 0) {
          console.log(`📥 [NotificationStore] All ${storedNotifications.length} notifications already exist (skipped)`);
          return { added: 0, skipped: skippedCount };
        }
        
        // Single state update for all new notifications
        set((state: NotificationState) => {
          const updatedNotifications = [...newNotifications, ...state.notifications];
          
          // Trim to max notifications and cleanup old ones
          const cleanedNotifications = updatedNotifications
            .filter((n: Notification) => Date.now() - n.timestamp < CLEANUP_MS)
            .slice(0, MAX_NOTIFICATIONS);
          
          return {
            ...state,
            notifications: cleanedNotifications,
          };
        });
        
        // Single unread count update after all integrations
        get()._updateUnreadCount();
        
        console.log(`📥 [NotificationStore] Batch integrated ${addedCount} notifications (${skippedCount} skipped as duplicates)`);
        return { added: addedCount, skipped: skippedCount };
      },

      markAsRead: (id: string) => {
        set((state: NotificationState) => ({
          ...state,
          notifications: state.notifications.map((notification: Notification) =>
            notification.id === id ? { ...notification, isRead: true } : notification
          ),
        }));
        
        // Update unread count using consistent filtering
        get()._updateUnreadCount();
        
        // 🔥 EVENT-DRIVEN: Clear real-time flags when reading notifications
        const state = get();
        if (state.hasRealtimeUpdates && state.unreadCount === 0) {
          get().clearRealtimeUpdates();
        }
      },

      markAllAsRead: () => {
        set((state: NotificationState) => ({
          ...state,
          notifications: state.notifications.map((notification: Notification) => ({
            ...notification,
            isRead: true,
          })),
          unreadCount: 0,
          // 🔥 EVENT-DRIVEN: Clear real-time flags when all notifications are read
          hasRealtimeUpdates: false,
          lastRealtimeUpdate: 0
        }));
      },

      clearNotification: (id: string) => {
        set((state: NotificationState) => {
          const updatedNotifications = state.notifications.filter((n: Notification) => n.id !== id);
          
          // Calculate filtered unread count
          const filteredUnreadCount = updatedNotifications
            .filter((n: Notification) => 
              !n.isRead && 
              (state.notificationSettings.enabledTypes[n.type] ?? true)
            )
            .length;
          
          return {
            ...state,
            notifications: updatedNotifications,
            unreadCount: filteredUnreadCount,
          };
        });
      },

      clearAllNotifications: () => {
        set((state: NotificationState) => ({
          ...state,
          notifications: [],
          unreadCount: 0,
        }));
      },

      hideNotification: (id: string) => {
        // Mark as hidden in persistent storage
        notificationHiddenState.hideNotification(id, 'hidden');
        
        // Also update local state for immediate UI feedback
        set((state: NotificationState) => ({
          ...state,
          notifications: state.notifications.map((notification: Notification) => 
            notification.id === id 
              ? { ...notification, isHidden: true }
              : notification
          ),
        }));
        
        // Update unread count to reflect hidden notification
        get()._updateUnreadCount();
      },

      dismissNotification: (id: string) => {
        const state = get();
        const notification = state.notifications.find((n: Notification) => n.id === id);
        
        // If notification was unread, mark it as read first (dismissing implies reading)
        if (notification && !notification.isRead) {
          // Mark as read locally and in database
          set((state: NotificationState) => ({
            ...state,
            notifications: state.notifications.map((n: Notification) => 
              n.id === id 
                ? { ...n, isRead: true, isDismissed: true }
                : n
            ),
          }));
          
          // Also mark as read in the database if it's a stored notification
          if (notification.metadata?.originalId) {
            // This is a stored notification, mark as read in database
            notificationApi.markAsRead(notification.metadata.originalId).catch((error: unknown) => {
              console.error('Failed to mark dismissed notification as read in database:', error);
            });
          }
        } else {
          // Just dismiss if already read
          set((state: NotificationState) => ({
            ...state,
            notifications: state.notifications.map((n: Notification) =>
              n.id === id
                ? { ...n, isDismissed: true }
                : n
            ),
          }));
        }
        
        // Mark as dismissed in persistent storage
        notificationHiddenState.hideNotification(id, 'dismissed');
        
        // Update unread count to reflect dismissed notification
        get()._updateUnreadCount();
      },

      hideAllNotifications: () => {
        const state = get();
        const visibleNotificationIds = state.notifications
          .filter((n: Notification) => !n.isHidden && !n.isDismissed)
          .map((n: Notification) => n.id);
        
        // Mark all as hidden in persistent storage
        notificationHiddenState.hideMultipleNotifications(visibleNotificationIds, 'hidden');
        
        // Also update local state for immediate UI feedback
        set((state: NotificationState) => ({
          ...state,
          notifications: state.notifications.map((notification: Notification) => ({
            ...notification,
            isHidden: true
          })),
        }));
        
        // Update unread count to reflect all hidden notifications
        get()._updateUnreadCount();
      },

      togglePanel: () => {
        set((state: NotificationState) => {
          const wasVisible = state.isVisible;
          const willBeVisible = !wasVisible;
          
          // If opening panel (industry standard: viewing implies reading)
          if (willBeVisible && !wasVisible) {
            console.log('🔔 [NotificationStore] Opening panel - auto-marking all notifications as read (industry standard UX)');
            
            // Count unread notifications before marking as read
            const unreadNotifications = state.notifications.filter((n: Notification) => !n.isRead);
            const localUnreadCount = unreadNotifications.length;
            
            console.log(`📋 [NotificationStore] Local unread: ${localUnreadCount}, Total notifications in store: ${state.notifications.length}`);
            
            // 🔥 FIXED: Always trigger backend sync when panel opens to handle API/local count mismatches
            // This ensures API unread count is cleared even if local notifications are already read
            setTimeout(() => {
              get().markAllAsReadOnOpen().catch((error: unknown) => {
                console.error('❌ [NotificationStore] Background sync failed:', error);
              });
            }, 0);
            
            if (localUnreadCount > 0) {
              console.log(`📖 [NotificationStore] Auto-marking ${localUnreadCount} local notifications as read`);
              
              // Mark all local notifications as read (industry standard behavior)
              const updatedNotifications = state.notifications.map(notification => ({
                ...notification,
                isRead: true,
              }));
              
              return {
                ...state,
                isVisible: true,
                notifications: updatedNotifications,
                unreadCount: 0, // Clear local badge immediately for better UX
              };
            } else {
              console.log('📖 [NotificationStore] No local unread notifications, but triggering backend sync to clear API count');
            }
          }
          
          // Just toggle visibility if closing or no unread notifications
          return {
            ...state,
            isVisible: !state.isVisible,
          };
        });
      },

      setVisible: (visible: boolean) => {
        set((state: NotificationState) => ({
          ...state,
          isVisible: visible,
        }));
      },
      
      // 🔥 NEW: Real-time tracking methods
      clearRealtimeUpdates: () => {
        set((state: NotificationState) => ({
          ...state,
          hasRealtimeUpdates: false,
          lastRealtimeUpdate: 0
        }));
      },

      // New notification settings methods
      updateNotificationSettings: (settings: Partial<NotificationSettings>) => {
        set((state: NotificationState) => ({
          ...state,
          notificationSettings: {
            ...state.notificationSettings,
            ...settings,
          },
        }));
      },

      setPanelMode: (mode: 'notifications' | 'settings') => {
        set((state: NotificationState) => ({ ...state, panelMode: mode }));
      },

      toggleNotificationType: (type: keyof NotificationSettings['enabledTypes']) => {
        set((state: NotificationState) => {
          const newSettings = {
            ...state.notificationSettings,
            enabledTypes: {
              ...state.notificationSettings.enabledTypes,
              [type]: !state.notificationSettings.enabledTypes[type],
            },
          };
          
          // Recalculate filtered unread count with new settings
          const filteredUnreadCount = state.notifications
            .filter((notification: Notification) => 
              !notification.isRead && 
              (newSettings.enabledTypes[notification.type] ?? true)
            )
            .length;
          
          return {
            ...state,
            notificationSettings: newSettings,
            unreadCount: filteredUnreadCount, // Update to use filtered count
          };
        });
      },

      resetNotificationSettings: () => {
        set((state: NotificationState) => {
          // Calculate filtered unread count with reset settings (all enabled)
          const filteredUnreadCount = state.notifications
            .filter((n: Notification) =>
              !n.isRead &&
              (DEFAULT_NOTIFICATION_SETTINGS.enabledTypes[n.type] ?? true)
            )
            .length;
          
          return {
            ...state,
            notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
            unreadCount: filteredUnreadCount,
          };
        });
      },

      getSortedNotifications: () => {
        const state = get();
        const { notifications, notificationSettings } = state;
        
        // Get persistently hidden notification IDs
        const hiddenIds = notificationHiddenState.getHiddenIds();
        
        return notifications
          .filter((notification: Notification) =>
            // Filter by settings
            (notificationSettings.enabledTypes[notification.type] ?? true) &&
            // Filter out locally hidden and dismissed notifications
            !notification.isHidden &&
            !notification.isDismissed &&
            // Filter out persistently hidden notifications
            !hiddenIds.has(notification.id)
          )
          .sort((a: Notification, b: Notification) => {
            if (a.isRead !== b.isRead) {
              return a.isRead ? 1 : -1;
            }
            return b.timestamp - a.timestamp;
          });
      },

      getFilteredUnreadCount: () => {
        const state = get();
        const { notifications, notificationSettings } = state;
        
        // Get persistently hidden notification IDs
        const hiddenIds = notificationHiddenState.getHiddenIds();
        
        return notifications
          .filter((notification: Notification) =>
            !notification.isRead &&
            (notificationSettings.enabledTypes[notification.type] ?? true) &&
            // Don't count locally hidden, dismissed, or persistently hidden notifications
            !notification.isHidden &&
            !notification.isDismissed &&
            !hiddenIds.has(notification.id)
          )
          .length;
      },

      // 🔥 NEW: Auto-read functionality for industry standard UX
      markAllAsReadOnOpen: async () => {
        console.log(`🔄 [NotificationStore] Starting backend sync to clear API unread count`);
        
        try {
          // Import the API function and trigger mark all as read
          const { notificationApi } = await import('../services/notificationApi');
          const response = await notificationApi.markAllAsRead(7); // 7 days back
          console.log(`✅ [NotificationStore] Backend mark-all-read completed: ${response.marked_count || 'unknown'} notifications marked`);
          
          // 🔥 WAIT FOR DB COMMIT: Add delay to ensure database transaction is committed
          await new Promise(resolve => setTimeout(resolve, 500)); // Increased from 200ms to 500ms
          
          // 🔥 FORCE REFRESH: Invalidate React Query cache to ensure fresh data with user-aware keys
          if (typeof window !== 'undefined' && (window as any).queryClient) {
            console.log('🔄 [NotificationStore] Force invalidating React Query cache after backend sync');
            const queryClient = (window as any).queryClient;
            
            // SECURITY: Use user-aware cache invalidation with specific query key matching
            // Target: ['notificationUnreadCount', userId] and ['notifications', userId]
            await queryClient.invalidateQueries({ 
              predicate: (query: any) => {
                const queryKey = query.queryKey;
                if (!Array.isArray(queryKey) || queryKey.length < 2) return false;
                
                // Match notification-related queries with user IDs
                const firstKey = queryKey[0];
                return (
                  firstKey === 'notificationUnreadCount' || 
                  firstKey === 'notifications' ||
                  firstKey === 'storedNotifications'
                );
              },
              refetchType: 'active' // Force immediate refetch of active queries
            });
            
            // Also reset queries to force fresh data fetch (more aggressive)
            await queryClient.resetQueries({
              predicate: (query: any) => {
                const queryKey = query.queryKey;
                if (!Array.isArray(queryKey) || queryKey.length < 2) return false;
                return queryKey[0] === 'notificationUnreadCount';
              }
            });
            
            console.log('✅ [NotificationStore] Cache invalidation and reset completed');
          }
          
          console.log('✅ [NotificationStore] Auto-read backend sync completed successfully');
          
        } catch (error) {
          console.error('❌ [NotificationStore] Auto-read backend sync failed:', error);
          // Don't throw error - local state is already updated for better UX
        }
      },

      // Internal methods
      _generateId: () => {
        return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      },

      _updateUnreadCount: () => {
        const state = get();
        // Use the same filtering logic as getFilteredUnreadCount for consistency
        const filteredUnreadCount = state.getFilteredUnreadCount();
        const prevCount = state.unreadCount;
        
        set({ unreadCount: filteredUnreadCount });
        
        // 🔥 EVENT-DRIVEN: Auto-clear real-time flags when count reaches 0
        if (filteredUnreadCount === 0 && state.hasRealtimeUpdates) {
          console.log('🔔 [NotificationStore] Unread count is 0 - clearing real-time flags');
          get().clearRealtimeUpdates();
        }
        
        // 🔥 DEBUG: Log unread count changes for debugging
        if (filteredUnreadCount !== prevCount) {
          console.log(`🔢 [NotificationStore] Unread count updated: ${prevCount} → ${filteredUnreadCount}`);
        }
      },

      _cleanupOldNotifications: () => {
        set((state: NotificationState) => {
          const now = Date.now();
          const cleanedNotifications = state.notifications
            .filter((n: Notification) => now - n.timestamp < CLEANUP_MS)
            .slice(0, MAX_NOTIFICATIONS);
          
          // Calculate filtered unread count
          const filteredUnreadCount = cleanedNotifications
            .filter((n: Notification) => 
              !n.isRead && 
              (state.notificationSettings.enabledTypes[n.type] ?? true)
            )
            .length;
          
          return {
            ...state,
            notifications: cleanedNotifications,
            unreadCount: filteredUnreadCount,
          };
        });
      },

      // 🔥 NEW: Stored notifications methods
      fetchStoredNotifications: async (options: { limit?: number; offset?: number; since?: Date; event_types?: string[]; unread_only?: boolean } = {}) => {
        set({ isLoadingStored: true, storedNotificationsError: null });
        
        try {
          console.log('🔄 [Notification Store] Fetching stored notifications...', options);
          
          const response = await notificationApi.getNotifications({
            limit: 50,
            ...options,
            // If we have a lastSyncTimestamp and no explicit since, use it
            since: options.since || (get().lastSyncTimestamp ? new Date(get().lastSyncTimestamp!) : undefined)
          });
          
          console.log(`✅ [Notification Store] Fetched ${response.notifications.length} stored notifications`);
          
          // Convert stored notifications to frontend format
          const convertedNotifications = response.notifications.map(stored => 
            notificationApiHelpers.convertStoredToFrontend(stored)
          );

          // Merge with existing notifications (avoid duplicates)
          const state = get();
          const existingIds = new Set(state.notifications.map((n: Notification) => n.id));
          const newNotifications = convertedNotifications.filter((n: Notification) => !existingIds.has(n.id));
          
          // Sort combined notifications by timestamp (newest first)
          const allNotifications = [...state.notifications, ...newNotifications]
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, MAX_NOTIFICATIONS); // Limit total notifications
          
          // Calculate new unread count
          const filteredUnreadCount = allNotifications
            .filter(n => 
              !n.isRead && 
              (state.notificationSettings.enabledTypes[n.type as keyof typeof state.notificationSettings.enabledTypes] ?? true)
            )
            .length;
          
          set({
            notifications: allNotifications,
            storedNotifications: response.notifications,
            lastSyncTimestamp: Date.now(),
            isLoadingStored: false,
            unreadCount: filteredUnreadCount,
            storedNotificationsError: null,
          });
          
          console.log(`🔔 [Notification Store] Added ${newNotifications.length} new notifications from storage (${allNotifications.length} total)`);
          
        } catch (error) {
          console.error('❌ [Notification Store] Failed to fetch stored notifications:', error);
          set({ 
            isLoadingStored: false, 
            storedNotificationsError: error instanceof Error ? error.message : 'Failed to fetch stored notifications'
          });
        }
      },

      syncWithStoredNotifications: async () => {
        console.log('🔄 [Notification Store] Syncing with stored notifications...');
        
        try {
          // Use the existing fetchStoredNotifications method with a recent timestamp
          // to only get notifications we might have missed
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          await get().fetchStoredNotifications({ 
            since: fiveMinutesAgo,
            limit: 100 // Get more recent notifications during sync
          });
          
          console.log('✅ [Notification Store] Sync with stored notifications complete');
          
        } catch (error) {
          console.error('❌ [Notification Store] Sync with stored notifications failed:', error);
        }
      },

      markStoredAsRead: async (id: string) => {
        try {
          // Extract the original ID from stored notification ID format
          const originalId = id.startsWith('stored_') ? id.replace('stored_', '') : id;
          
          console.log(`📖 [Notification Store] Marking stored notification as read: ${originalId}`);
          
          await notificationApi.markAsRead(originalId);
          
          // Update local state
          set((state: NotificationState) => ({
            ...state,
            notifications: state.notifications.map((notification: Notification) =>
              notification.id === id ? { ...notification, isRead: true } : notification
            ),
          }));
          
          // Update unread count
          get()._updateUnreadCount();
          
          console.log(`✅ [Notification Store] Stored notification ${originalId} marked as read`);
          
        } catch (error) {
          console.error('❌ [Notification Store] Failed to mark stored notification as read:', error);
          throw error;
        }
      },

      clearStoredNotificationsError: () => {
        set({ storedNotificationsError: null });
      },

      // 🔥 NEW: Deduplication helper methods
      isDuplicateOfSSEEvent: (stored: StoredNotification): boolean => {
        const state = get();
        const DEDUPLICATION_WINDOW = 30 * 60 * 1000; // 30 minutes
        
        // Check if we have recent SSE events for same symbol/type
        const recentSSEEvents = state.notifications.filter((n: Notification) => 
          n.source === 'sse' && 
          n.metadata?.symbol === stored.data?.symbol &&
          n.type === 'milestone' &&
          stored.event_type === 'position_milestone' &&
          (Date.now() - n.timestamp) < DEDUPLICATION_WINDOW
        );
        
        return recentSSEEvents.length > 0;
      },

      isRecoveryNotification: (stored: StoredNotification): boolean => {
        const state = get();
        const eventTime = new Date(stored.event_timestamp).getTime();
        const GRACE_PERIOD = 5 * 60 * 1000; // 5 minutes grace period
        
        // 🔥 RELAXED FILTER: Allow recent events even if during current session
        // This prevents loss of notifications due to localStorage timing issues
        return (
          eventTime < state.sessionStartTime || // Traditional recovery (while away)
          Date.now() - eventTime < GRACE_PERIOD // Recent event (grace period)
        );
      },
    }),
    {
      name: 'notification-storage',
      storage: createJSONStorage(() => debouncedUserAwareNotificationStorage),
      version: 2, // Bump version to invalidate old global data
      // 🔥 OPTIMIZED: Throttle persistence to reduce localStorage writes
      // This prevents the 100+ writes seen during batch integration
      skipHydration: false,
      merge: (persistedState: any, currentState: any) => {
        // Smart merge to prevent unnecessary writes
        return {
          ...currentState,
          ...persistedState,
          // Always use current state for these runtime values
          isVisible: currentState.isVisible,
          panelMode: currentState.panelMode,
          hasRealtimeUpdates: currentState.hasRealtimeUpdates,
          lastRealtimeUpdate: currentState.lastRealtimeUpdate,
        };
      },
      partialize: (state) => ({
        notifications: state.notifications,
        notificationSettings: state.notificationSettings,
        lastSyncTimestamp: state.lastSyncTimestamp,
        sessionStartTime: state.sessionStartTime,
        // Don't persist UI state like isVisible, panelMode, loading states, or errors
      }),
      onRehydrateStorage: () => (state) => {
        // Update unread count and cleanup old notifications on rehydration
        if (state) {
          // SECURITY: Log rehydration with user context
          if (currentNotificationUserId) {
            console.log(`💾 [Notification Store] Rehydrated for user: ${currentNotificationUserId.substring(0, 20)}... (${state.notifications.length} notifications)`);
          } else {
            console.log(`💾 [Notification Store] Rehydrated (no user context yet)`);
          }
          
          state._updateUnreadCount();
          state._cleanupOldNotifications();
          
          // Clear any error states that shouldn't persist
          state.storedNotificationsError = null;
          state.isLoadingStored = false;
        }
      },
    }
  )
);